/**
 * VotingRollEditor — the eligibility roll for a session.
 * Add voters one at a time or paste a bulk list (CSV: key,name,ref,weight).
 * Issue scoped vote-by-link tokens for async sessions / guests.
 */

'use client';

/**
 * USER DOCS: changes to this component should be accompanied by a
 * corresponding update to the user guide:
 * next_portal/user-docs/content/docs/total-operations/workflows/digital-voting/agenda-and-voter-roll.mdx
 * (published at user-docs.totalaccess.co.za/total-operations/workflows/digital-voting/agenda-and-voter-roll)
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Building2,
  CalendarClock,
  CheckCircle2,
  ClipboardCopy,
  KeyRound,
  Loader2,
  MailWarning,
  Plus,
  Trash2,
  UserCheck,
  Users,
} from 'lucide-react';
import {
  getVotingPolicy,
  importResidentRoll,
  issueVoterTokens,
  listVoters,
  previewResidentRoll,
  sendArrearsNotice,
  sendSessionInvites,
  upsertVoters,
  type ArrearsNoticeResult,
  type ResidentRollPreview,
  type SessionInviteResult,
  type VoterToken,
  type VotingSession,
  type VotingVoter,
} from './votingApi';
import { Badge, formatDateTime } from './shared';
import { notify } from '@/lib/notifications';
import UserDocsLink from '@/components/shared/UserDocsLink';
import { listLocationGroups, type LocationGroup } from '@/lib/locationGroupsApi';
import { VOTER_STAKEHOLDER_SEGMENTS } from '@/lib/property/vocabulary';

// Every role the electorate segments can match — passed to the roll
// derivation so stakeholder people (portal_role / parent contact_type / any
// linked role_at_location) appear even without a unit link.
const VOTER_SEGMENT_ROLES = [...new Set(VOTER_STAKEHOLDER_SEGMENTS.flatMap((s) => s.roles))];

interface Props {
  session: VotingSession;
  quorum: { eligible: number; present: number; requiredPct: number | null; met: boolean | null } | null;
  canManage: boolean;
  onChanged: () => void;
}

const inputCls =
  'rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500';

export default function VotingRollEditor({ session, quorum, canManage, onChanged }: Props) {
  const [voters, setVoters] = useState<VotingVoter[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [key, setKey] = useState('');
  const [name, setName] = useState('');
  const [ref, setRef] = useState('');
  const [weight, setWeight] = useState('1');
  const [proxy, setProxy] = useState('');
  const [bulk, setBulk] = useState('');
  const [showBulk, setShowBulk] = useState(false);
  const [busy, setBusy] = useState(false);
  const [issued, setIssued] = useState<VoterToken[]>([]);
  const [inviteResult, setInviteResult] = useState<SessionInviteResult | null>(null);
  const [arrearsResult, setArrearsResult] = useState<ArrearsNoticeResult | null>(null);

  // Resident import (contact_people_locations-derived roll)
  const [showResidents, setShowResidents] = useState(false);
  const [residentPreview, setResidentPreview] = useState<ResidentRollPreview | null>(null);
  const [residentLoading, setResidentLoading] = useState(false);
  const [residentSelected, setResidentSelected] = useState<Set<number>>(new Set());
  const [arrearsPolicy, setArrearsPolicy] = useState<'ineligible' | 'exclude' | 'ignore'>('ineligible');

  // The client's voting policy supplies the default arrears handling —
  // yaml config.voter_roll.arrears_policy → custom_settings.voting.voter_roll
  // → this per-import choice.
  useEffect(() => {
    void getVotingPolicy().then((policy) => {
      const configured = policy?.voter_roll?.arrears_policy;
      if (configured) setArrearsPolicy(configured);
    });
  }, []);
  // Scope filters — narrow the import to specific units/phases (location_ids)
  // and/or roles before picking individuals. Empty sets mean "no filter".
  const [locationFilter, setLocationFilter] = useState<Set<number>>(new Set());
  const [roleFilter, setRoleFilter] = useState<Set<string>>(new Set());
  const [residentSearch, setResidentSearch] = useState('');
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  // Property groups (contact_location_groups) — one-click scope to a named
  // set of units (phase/street/zone) rather than ticking units one by one.
  const [locationGroups, setLocationGroups] = useState<LocationGroup[]>([]);
  // When the session is linked to an estate/property, the roll defaults to
  // that scheme (site + units + members under the same owning contact).
  const [propertyScope, setPropertyScope] = useState(true);

  const editable = canManage && !['closed', 'cancelled'].includes(session.status);

  const load = useCallback(async (background = false) => {
    if (!background) setLoading(true);
    try {
      setVoters(await listVoters(session.id));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load roll');
    } finally {
      setLoading(false);
    }
  }, [session.id]);

  useEffect(() => {
    void load();
  }, [load]);

  // Check-ins move while the session is live — keep the roll fresh without
  // flashing the loading spinner on every poll.
  useEffect(() => {
    if (session.status !== 'live') return;
    const t = setInterval(() => void load(true), 5000);
    return () => clearInterval(t);
  }, [session.status, load]);

  const eligibleCount = useMemo(() => voters.filter((v) => v.eligible).length, [voters]);
  const ineligibleCount = voters.length - eligibleCount;
  const checkedIn = useMemo(() => voters.filter((v) => v.checked_in_at).length, [voters]);

  // Distinct locations/roles in the preview — drives the scope filters. One
  // chosen location per resident, so a phase picker is just a unit subset.
  const residentLocations = useMemo(() => {
    const map = new Map<number, string>();
    for (const r of residentPreview?.residents ?? []) {
      if (r.location_id != null && !map.has(r.location_id)) map.set(r.location_id, r.location_name);
    }
    return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [residentPreview]);

  // Stakeholder audiences — the same segments the bulk emailer resolves
  // (owners/tenants/residents/trustees/board members), matched server-side
  // via matched_roles. One click selects every member, incl. people with no
  // unit link.
  const residentAudiences = useMemo(() => {
    const rows = residentPreview?.residents ?? [];
    return VOTER_STAKEHOLDER_SEGMENTS.map((seg) => ({
      seg,
      memberIds: rows
        .filter((r) => r.matched_roles?.some((x) => seg.roles.includes(x)))
        .map((r) => r.person_id),
    })).filter((a) => a.memberIds.length > 0);
  }, [residentPreview]);

  const residentRoles = useMemo(() => {
    const set = new Set<string>();
    for (const r of residentPreview?.residents ?? []) {
      if (r.role_at_location) set.add(r.role_at_location);
    }
    return [...set].sort();
  }, [residentPreview]);

  const previewLocationIds = useMemo(
    () => new Set(residentLocations.map(([id]) => id)),
    [residentLocations],
  );

  // Groups with at least one member location present in the preview — these
  // get a 1-click scope chip in the filter row.
  const applicableGroups = useMemo(
    () =>
      locationGroups
        .map((g) => ({
          group: g,
          scopeIds: new Set(g.location_ids.filter((id) => previewLocationIds.has(id))),
        }))
        .filter(({ scopeIds }) => scopeIds.size > 0),
    [locationGroups, previewLocationIds],
  );

  const filteredResidents = useMemo(() => {
    const rows = residentPreview?.residents ?? [];
    const term = residentSearch.trim().toLowerCase();
    return rows.filter((r) => {
      if (locationFilter.size > 0 && (r.location_id == null || !locationFilter.has(r.location_id))) {
        return false;
      }
      if (roleFilter.size > 0 && !roleFilter.has(r.role_at_location ?? '')) return false;
      if (
        term &&
        !r.display_name.toLowerCase().includes(term) &&
        !r.member_ref.toLowerCase().includes(term) &&
        !r.voter_key.toLowerCase().includes(term)
      ) {
        return false;
      }
      return true;
    });
  }, [residentPreview, locationFilter, roleFilter, residentSearch]);

  const addVoter = async () => {
    if (!key.trim() || busy) return;
    setBusy(true);
    try {
      await upsertVoters(session.id, [
        {
          voter_key: key.trim(),
          display_name: name.trim() || undefined,
          member_ref: ref.trim() || undefined,
          weight: Number(weight) || 1,
          proxy_of: proxy.trim() || undefined,
        },
      ]);
      setKey('');
      setName('');
      setRef('');
      setWeight('1');
      setProxy('');
      await load();
      onChanged();
    } catch (err) {
      notify.error(err instanceof Error ? err.message : 'Failed to add voter');
    } finally {
      setBusy(false);
    }
  };

  const addBulk = async () => {
    if (!bulk.trim() || busy) return;
    setBusy(true);
    try {
      const rows = bulk
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean)
        .map((line) => {
          const [voter_key, display_name, member_ref, w, proxy_of] = line.split(',').map((c) => c?.trim());
          return { voter_key, display_name, member_ref, weight: Number(w) || 1, proxy_of: proxy_of || undefined };
        })
        .filter((v) => v.voter_key);
      if (rows.length === 0) {
        notify.error('No valid rows — expected: voter_key, name, member_ref, weight[, proxy_of]');
        return;
      }
      const n = await upsertVoters(session.id, rows);
      notify.success(`${n} voter${n === 1 ? '' : 's'} added to the roll`);
      setBulk('');
      setShowBulk(false);
      await load();
      onChanged();
    } catch (err) {
      notify.error(err instanceof Error ? err.message : 'Bulk import failed');
    } finally {
      setBusy(false);
    }
  };

  const toggleEligible = async (voter: VotingVoter) => {
    try {
      // The upsert merge overwrites every column — resend the full row.
      await upsertVoters(session.id, [
        {
          voter_key: voter.voter_key,
          display_name: voter.display_name ?? undefined,
          member_ref: voter.member_ref ?? undefined,
          weight: voter.weight,
          proxy_of: voter.proxy_of ?? undefined,
          eligible: !voter.eligible,
        },
      ]);
      await load();
      onChanged();
    } catch (err) {
      notify.error(err instanceof Error ? err.message : 'Failed to update voter');
    }
  };

  const loadResidentPreview = async (scope: boolean) => {
    setResidentLoading(true);
    try {
      const preview = await previewResidentRoll(session.id, {
        segments: VOTER_SEGMENT_ROLES,
        property_scope: scope && session.location_id != null,
      });
      setResidentPreview(preview);
      // Default selection: everyone who qualifies (active account, no arrears).
      setResidentSelected(new Set(preview.residents.filter((r) => r.qualifies).map((r) => r.person_id)));
    } catch (err) {
      notify.error(err instanceof Error ? err.message : 'Failed to load residents');
      setShowResidents(false);
    } finally {
      setResidentLoading(false);
    }
  };

  const openResidentImport = async () => {
    if (showResidents) {
      setShowResidents(false);
      return;
    }
    setShowResidents(true);
    setLocationFilter(new Set());
    setRoleFilter(new Set());
    setResidentSearch('');
    setShowLocationPicker(false);
    // Groups are a nicety — never block the import panel if they fail.
    const groups = await listLocationGroups().catch(() => [] as LocationGroup[]);
    setLocationGroups(groups);
    await loadResidentPreview(propertyScope);
  };

  const toggleAudienceMembers = (memberIds: number[]) => {
    const allSelected =
      memberIds.length > 0 && memberIds.every((id) => residentSelected.has(id));
    setResidentSelected((prev) => {
      const next = new Set(prev);
      for (const id of memberIds) {
        if (allSelected) next.delete(id);
        else next.add(id);
      }
      return next;
    });
  };

  const toggleResident = (personId: number) => {
    setResidentSelected((prev) => {
      const next = new Set(prev);
      if (next.has(personId)) next.delete(personId);
      else next.add(personId);
      return next;
    });
  };

  const runResidentImport = async () => {
    if (residentSelected.size === 0 || busy) return;
    setBusy(true);
    try {
      const result = await importResidentRoll(session.id, {
        person_ids: [...residentSelected],
        segments: VOTER_SEGMENT_ROLES,
        property_scope: propertyScope && session.location_id != null,
        arrears: arrearsPolicy,
      });
      notify.success(
        `Imported ${result.imported} voter${result.imported === 1 ? '' : 's'}` +
          (result.flagged_ineligible ? ` (${result.flagged_ineligible} flagged ineligible)` : '') +
          (result.skipped ? `, ${result.skipped} excluded` : ''),
      );
      setShowResidents(false);
      setResidentPreview(null);
      await load();
      onChanged();
    } catch (err) {
      notify.error(err instanceof Error ? err.message : 'Resident import failed');
    } finally {
      setBusy(false);
    }
  };

  const issueTokens = async () => {
    setBusy(true);
    try {
      const tokens = await issueVoterTokens(session.id);
      setIssued(tokens);
      if (tokens.length === 0) {
        notify.info('Every eligible voter already has a token');
      } else {
        notify.success(`Issued ${tokens.length} vote-by-link token${tokens.length === 1 ? '' : 's'}`);
      }
      await load();
    } catch (err) {
      notify.error(err instanceof Error ? err.message : 'Failed to issue tokens');
    } finally {
      setBusy(false);
    }
  };

  // Emails the roll a METHOD:REQUEST calendar invite — lands as an event in
  // Gmail/Outlook with the voter's ballot link. Needs a schedule on the
  // session so the invite has a start time.
  const hasSchedule = session.scheduled_start != null || session.voting_opens_at != null;

  const sendInvites = async () => {
    setBusy(true);
    try {
      const result = await sendSessionInvites(session.id);
      setInviteResult(result);
      if (result.invited > 0) {
        notify.success(`Sent ${result.invited} calendar invite${result.invited === 1 ? '' : 's'}`);
      } else {
        notify.info('No voter emails could be resolved — see skipped list below');
      }
    } catch (err) {
      notify.error(err instanceof Error ? err.message : 'Failed to send invites');
    } finally {
      setBusy(false);
    }
  };

  // Queues an 'Arrears Reminder' campaign through Communications Management
  // to every ineligible roll member — they were on the invite list, so they
  // should hear they're barred before voting day, not at the ballot.
  const sendArrears = async () => {
    setBusy(true);
    try {
      const result = await sendArrearsNotice(session.id);
      setArrearsResult(result);
      notify.success(
        `Arrears notice queued to ${result.queued} voter${result.queued === 1 ? '' : 's'}` +
          (result.skipped.length > 0 ? ` — ${result.skipped.length} had no email` : ''),
      );
    } catch (err) {
      notify.error(err instanceof Error ? err.message : 'Failed to send arrears notice');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="h-full overflow-y-auto p-4">
      {/* Quorum strip */}
      {session.quorum_required && (
        <div className="mb-3 flex items-center gap-3 rounded-lg border border-gray-200 bg-white px-4 py-2.5">
          <Users className="h-4 w-4 text-blue-600" />
          <span className="text-sm text-gray-700">
            {session.delivery_mode === 'async' ? 'Turnout' : 'Present'} {quorum?.present ?? checkedIn}
            /{quorum?.eligible ?? eligibleCount} eligible
            {session.quorum_percent != null && ` · quorum ${session.quorum_percent}%`}
          </span>
          {quorum?.met != null && (
            <Badge
              label={quorum.met ? 'Quorum met' : 'Below quorum'}
              color={quorum.met ? '#166534' : '#b45309'}
              bg={quorum.met ? '#dcfce7' : '#fef3c7'}
            />
          )}
          <UserDocsLink docsPath="/total-operations/workflows/digital-voting/agenda-and-voter-roll#the-participants-tab--the-voter-roll" label="User guide: Agenda and voter roll" className="ml-auto" />
        </div>
      )}

      {/* Add voter */}
      {editable && (
        <div className="mb-3 space-y-2 rounded-lg border border-gray-200 bg-white p-3">
          <div className="flex flex-wrap items-center gap-2">
            <input
              className={`${inputCls} w-44`}
              placeholder="voter key (e.g. unit-12)"
              value={key}
              onChange={(e) => setKey(e.target.value)}
            />
            <input
              className={`${inputCls} w-44`}
              placeholder="Display name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <input
              className={`${inputCls} w-32`}
              placeholder="Member ref"
              value={ref}
              onChange={(e) => setRef(e.target.value)}
            />
            <input
              className={`${inputCls} w-20`}
              type="number"
              min={0}
              step="0.001"
              title="Vote weight (levy/ownership)"
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
            />
            <input
              className={`${inputCls} w-40`}
              placeholder="Proxy to voter_key"
              title="Delegate this vote to another voter_key — that voter's ballot carries this weight too. Both parties are emailed an official proxy notice."
              value={proxy}
              onChange={(e) => setProxy(e.target.value)}
            />
            <button
              onClick={() => void addVoter()}
              disabled={!key.trim() || busy}
              className="inline-flex items-center gap-1 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              <Plus className="h-4 w-4" /> Add
            </button>
            <button
              onClick={() => setShowBulk((v) => !v)}
              className="text-sm font-medium text-blue-600 hover:text-blue-800"
            >
              {showBulk ? 'Hide bulk import' : 'Bulk import'}
            </button>
            <button
              onClick={() => void openResidentImport()}
              className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-800"
              title="Build the roll from residents linked to units/stands in Contacts"
            >
              <Building2 className="h-4 w-4" />
              {showResidents ? 'Hide residents' : 'Import residents'}
            </button>
            {hasSchedule && (
              <button
                onClick={() => void sendInvites()}
                disabled={busy || eligibleCount === 0}
                className="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-blue-300 px-3 py-2 text-sm font-medium text-blue-700 hover:bg-blue-50 disabled:opacity-50"
                title="Email every eligible voter a calendar invite (.ics) with their ballot link"
              >
                <CalendarClock className="h-4 w-4" /> Email invites
              </button>
            )}
            {(session.delivery_mode === 'async' || session.voting_opens_at != null) && (
              <button
                onClick={() => void issueTokens()}
                disabled={busy || eligibleCount === 0}
                className={`${hasSchedule ? '' : 'ml-auto '}inline-flex items-center gap-1.5 rounded-lg border border-blue-300 px-3 py-2 text-sm font-medium text-blue-700 hover:bg-blue-50 disabled:opacity-50`}
                title="Issue per-voter ballot links for the pre-voting window"
              >
                <KeyRound className="h-4 w-4" /> Issue vote links
              </button>
            )}
            {ineligibleCount > 0 && (
              <button
                onClick={() => void sendArrears()}
                disabled={busy}
                className={`${
                  !hasSchedule && session.delivery_mode !== 'async' && session.voting_opens_at == null
                    ? 'ml-auto '
                    : ''
                }inline-flex items-center gap-1.5 rounded-lg border border-amber-300 px-3 py-2 text-sm font-medium text-amber-700 hover:bg-amber-50 disabled:opacity-50`}
                title="Email every ineligible (in-arrears) voter an arrears notice — queued as an Arrears Reminder campaign in Communications"
              >
                <MailWarning className="h-4 w-4" /> Notify in arrears ({ineligibleCount})
              </button>
            )}
          </div>
          {showBulk && (
            <div>
              <textarea
                className="w-full rounded-lg border border-gray-300 px-3 py-2 font-mono text-xs focus:border-blue-500 focus:outline-none"
                rows={5}
                placeholder={'unit-1,John Smith,ERF-101,1\nunit-2,Jane Doe,ERF-102,1.5\nuser-42,Peter Jones,,1'}
                value={bulk}
                onChange={(e) => setBulk(e.target.value)}
              />
              <div className="mt-1 flex items-center justify-between">
                <p className="text-xs text-gray-400">
                  One per line: voter_key, display_name, member_ref, weight[, proxy_of]. Use user-{'{id}'} for
                  portal members. proxy_of = voter_key of the delegate who casts this vote — assigning or
                  revoking a proxy emails an official notice to both parties.
                </p>
                <button
                  onClick={() => void addBulk()}
                  disabled={!bulk.trim() || busy}
                  className="rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  Import
                </button>
              </div>
            </div>
          )}

          {/* Resident import — derive the roll from contact_people_locations.
              Qualification = contact account active + no outstanding balance;
              non-qualifying residents can be flagged or excluded. */}
          {showResidents && (
            <div className="rounded-lg border border-blue-200 bg-blue-50/50 p-3">
              {residentLoading || !residentPreview ? (
                <div className="flex items-center gap-2 py-4 text-xs text-gray-500">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading residents from contacts…
                </div>
              ) : residentPreview.residents.length === 0 ? (
                <p className="py-2 text-xs text-gray-500">
                  No eligible people found — link people to units/stands in
                  Contacts, or give them a stakeholder role (owner, resident,
                  tenant, trustee, board member).
                </p>
              ) : (
                <>
                  <div className="mb-2 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-gray-600">
                    <span>
                      {filteredResidents.length} of {residentPreview.counts.total} residents
                      {locationFilter.size > 0 && ` · ${locationFilter.size} location${locationFilter.size === 1 ? '' : 's'}`}
                      {' · '}{residentPreview.counts.qualifying} qualifying
                      {residentPreview.counts.in_arrears > 0 && ` · ${residentPreview.counts.in_arrears} in arrears`}
                      {` · ${residentPreview.counts.with_login} with app login`}
                      {residentPreview.counts.missing_contact > 0 && (
                        <span className="font-medium text-amber-600">
                          {' · '}{residentPreview.counts.missing_contact} missing email/phone
                        </span>
                      )}
                    </span>
                    <div className="ml-auto flex items-center gap-3">
                      <span className="font-medium">Non-qualifying:</span>
                      {(
                        [
                          ['ineligible', 'Import as ineligible'],
                          ['exclude', 'Exclude'],
                          ['ignore', 'Import as eligible'],
                        ] as const
                      ).map(([value, label]) => (
                        <label key={value} className="flex items-center gap-1">
                          <input
                            type="radio"
                            name="arrears-policy"
                            checked={arrearsPolicy === value}
                            onChange={() => setArrearsPolicy(value)}
                          />
                          {label}
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Scope filters — limit the import to specific units/phases
                      and/or roles, then pick individuals within that scope. */}
                  <div className="mb-2 flex flex-wrap items-center gap-2">
                    <input
                      className="w-44 rounded-lg border border-gray-300 px-2.5 py-1 text-xs focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      placeholder="Search name / unit…"
                      value={residentSearch}
                      onChange={(e) => setResidentSearch(e.target.value)}
                    />
                    {/* Property scope — the session's linked estate. On by
                        default; clicking widens the roll to the whole
                        portfolio (all schemes under this client). */}
                    {session.location_id != null && (
                      <button
                        type="button"
                        title={
                          propertyScope
                            ? 'Scoped to the linked property — click to include every scheme'
                            : 'Showing the whole portfolio — click to scope to the linked property'
                        }
                        disabled={residentLoading}
                        onClick={() => {
                          const next = !propertyScope;
                          setPropertyScope(next);
                          setLocationFilter(new Set());
                          void loadResidentPreview(next);
                        }}
                        className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${
                          propertyScope
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : 'border-gray-300 text-gray-500 hover:border-gray-400'
                        }`}
                      >
                        <Building2 className="h-3 w-3" />
                        {session.location_name ?? 'Linked property'}
                      </button>
                    )}
                    {residentLocations.length > 1 && (
                      <button
                        type="button"
                        onClick={() => setShowLocationPicker((v) => !v)}
                        className={`inline-flex items-center gap-1 rounded-lg border px-2 py-1 text-xs font-medium ${
                          locationFilter.size > 0
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : 'border-gray-300 text-gray-600 hover:border-gray-400'
                        }`}
                      >
                        <Building2 className="h-3.5 w-3.5" />
                        {locationFilter.size === 0
                          ? `All ${residentLocations.length} units/locations`
                          : `${locationFilter.size} of ${residentLocations.length} selected`}
                      </button>
                    )}
                    {/* Stakeholder audiences — same segments the bulk emailer
                        offers; one click selects every matched person. */}
                    {residentAudiences.map(({ seg, memberIds }) => {
                      const allSelected = memberIds.every((id) => residentSelected.has(id));
                      return (
                        <button
                          key={seg.key}
                          type="button"
                          title={`${allSelected ? 'Deselect' : 'Select'} ${memberIds.length} people — ${seg.label}`}
                          onClick={() => toggleAudienceMembers(memberIds)}
                          className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${
                            allSelected
                              ? 'border-blue-500 bg-blue-50 text-blue-700'
                              : 'border-gray-300 text-gray-500 hover:border-gray-400'
                          }`}
                        >
                          <Users className="h-3 w-3" />
                          {seg.label} ({memberIds.length})
                        </button>
                      );
                    })}
                    {/* Property groups — 1-click scope to a named property set
                        (phase/street/zone) instead of ticking units. */}
                    {applicableGroups.map(({ group: g, scopeIds }) => {
                      const active =
                        scopeIds.size === locationFilter.size &&
                        [...scopeIds].every((id) => locationFilter.has(id));
                      return (
                        <button
                          key={g.id}
                          type="button"
                          title={`Scope to "${g.name}" (${scopeIds.size} properties)`}
                          onClick={() => setLocationFilter(active ? new Set() : new Set(scopeIds))}
                          className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${
                            active
                              ? 'border-blue-500 bg-blue-50 text-blue-700'
                              : 'border-gray-300 text-gray-500 hover:border-gray-400'
                          }`}
                        >
                          <span className="h-1.5 w-1.5 rounded-full" style={{ background: g.color || '#94a3b8' }} />
                          {g.name}
                        </button>
                      );
                    })}
                    {residentRoles.map((role) => {
                      const active = roleFilter.has(role);
                      return (
                        <button
                          key={role}
                          type="button"
                          onClick={() =>
                            setRoleFilter((prev) => {
                              const next = new Set(prev);
                              if (next.has(role)) next.delete(role);
                              else next.add(role);
                              return next;
                            })
                          }
                          className={`rounded-full border px-2 py-0.5 text-[11px] font-medium capitalize ${
                            active
                              ? 'border-blue-500 bg-blue-50 text-blue-700'
                              : 'border-gray-300 text-gray-500 hover:border-gray-400'
                          }`}
                        >
                          {role}
                        </button>
                      );
                    })}
                    {(locationFilter.size > 0 || roleFilter.size > 0 || residentSearch) && (
                      <button
                        type="button"
                        onClick={() => {
                          setLocationFilter(new Set());
                          setRoleFilter(new Set());
                          setResidentSearch('');
                        }}
                        className="text-[11px] font-medium text-blue-600 hover:text-blue-800"
                      >
                        Clear filters
                      </button>
                    )}
                  </div>

                  {showLocationPicker && residentLocations.length > 1 && (
                    <div className="mb-2 rounded-lg border border-blue-200 bg-white p-2">
                      <div className="mb-1.5 flex items-center justify-between text-[11px] text-gray-500">
                        <span>Units / locations included in this vote (e.g. a phase)</span>
                        <span className="flex gap-2">
                          <button
                            type="button"
                            className="font-medium text-blue-600 hover:text-blue-800"
                            onClick={() => setLocationFilter(new Set(residentLocations.map(([id]) => id)))}
                          >
                            All
                          </button>
                          <button
                            type="button"
                            className="font-medium text-blue-600 hover:text-blue-800"
                            onClick={() => setLocationFilter(new Set())}
                          >
                            None
                          </button>
                        </span>
                      </div>
                      <div className="grid max-h-32 grid-cols-2 gap-x-3 gap-y-1 overflow-y-auto sm:grid-cols-3 lg:grid-cols-4">
                        {residentLocations.map(([id, name]) => (
                          <label key={id} className="flex items-center gap-1.5 text-xs text-gray-700">
                            <input
                              type="checkbox"
                              checked={locationFilter.has(id)}
                              onChange={() =>
                                setLocationFilter((prev) => {
                                  const next = new Set(prev);
                                  if (next.has(id)) next.delete(id);
                                  else next.add(id);
                                  return next;
                                })
                              }
                              className="rounded text-blue-600"
                            />
                            <span className="truncate">{name}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="max-h-64 overflow-y-auto rounded-lg border border-blue-100 bg-white">
                    <table className="w-full text-left text-xs">
                      <thead>
                        <tr className="border-b border-gray-200 text-[10px] uppercase tracking-wide text-gray-400">
                          <th className="py-1.5 pl-3 pr-2 font-medium"></th>
                          <th className="py-1.5 pr-3 font-medium">Resident</th>
                          <th className="py-1.5 pr-3 font-medium">Unit / ref</th>
                          <th className="py-1.5 pr-3 font-medium">Role</th>
                          <th className="py-1.5 pr-3 font-medium">Account</th>
                          <th
                            className="py-1.5 pr-3 font-medium"
                            title="How the voter identifies: portal login, or a 6-digit code sent to their registered channel (/vote/identify)"
                          >
                            Verify
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredResidents.length === 0 && (
                          <tr>
                            <td colSpan={6} className="py-6 text-center text-xs text-gray-400">
                              No residents match the current scope filters.
                            </td>
                          </tr>
                        )}
                        {filteredResidents.map((r) => (
                          <tr key={r.person_id} className="border-b border-gray-100 last:border-0">
                            <td className="py-1.5 pl-3 pr-2">
                              <input
                                type="checkbox"
                                checked={residentSelected.has(r.person_id)}
                                onChange={() => toggleResident(r.person_id)}
                                className="rounded text-blue-600"
                              />
                            </td>
                            <td className="py-1.5 pr-3">
                              <div className="font-medium text-gray-900">{r.display_name}</div>
                              <div className="font-mono text-[10px] text-gray-400">{r.voter_key}</div>
                            </td>
                            <td className="py-1.5 pr-3 text-gray-600">{r.member_ref}</td>
                            <td className="py-1.5 pr-3 text-gray-600">{r.role_at_location || '—'}</td>
                            <td className="py-1.5 pr-3">
                              {r.qualifies ? (
                                <Badge label="Qualifies" color="#166534" bg="#dcfce7" />
                              ) : (
                                <Badge
                                  label={r.in_arrears ? 'In arrears' : r.account_status || 'inactive'}
                                  color="#b45309"
                                  bg="#fef3c7"
                                />
                              )}
                              {r.already_on_roll && (
                                <span className="ml-1 text-[10px] text-gray-400">(on roll)</span>
                              )}
                            </td>
                            <td className="py-1.5 pr-3 text-gray-600">
                              {r.has_login ? (
                                'In-app'
                              ) : r.verifiable ? (
                                `OTP: ${[
                                  r.has_whatsapp ? 'WhatsApp' : r.has_phone ? 'SMS' : null,
                                  r.has_email ? 'email' : null,
                                ]
                                  .filter(Boolean)
                                  .join(' + ')}`
                              ) : (
                                <span
                                  className="font-medium text-amber-600"
                                  title="No email or phone on the contact record — add one in Contacts or issue a vote link manually"
                                >
                                  No email/phone
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="mt-2 flex items-center justify-between">
                    <label className="flex items-center gap-1.5 text-xs text-gray-500">
                      <input
                        type="checkbox"
                        checked={
                          filteredResidents.length > 0 &&
                          filteredResidents.every((r) => residentSelected.has(r.person_id))
                        }
                        onChange={(e) =>
                          setResidentSelected(
                            e.target.checked
                              ? new Set(filteredResidents.map((r) => r.person_id))
                              : new Set(filteredResidents.filter((r) => r.qualifies).map((r) => r.person_id)),
                          )
                        }
                        className="rounded text-blue-600"
                      />
                      Select all in scope
                    </label>
                    <button
                      onClick={() => void runResidentImport()}
                      disabled={residentSelected.size === 0 || busy}
                      className="rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                    >
                      {busy ? 'Importing…' : `Import ${residentSelected.size} voter${residentSelected.size === 1 ? '' : 's'}`}
                    </button>
                  </div>
                  <p className="mt-1 text-[10px] text-gray-400">
                    Residents with an app login vote in My Ballots; others get a
                    vote-by-link token (Issue vote links above for async sessions).
                  </p>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Issued tokens */}
      {issued.length > 0 && (
        <div className="mb-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3">
          <p className="mb-2 text-xs font-semibold text-emerald-800">
            {issued.length} new vote link{issued.length === 1 ? '' : 's'} — share each privately
            (they are shown once):
          </p>
          <div className="max-h-40 space-y-1 overflow-y-auto">
            {issued.map((t) => (
              <div key={t.voter_key} className="flex items-center gap-2 text-xs">
                <span className="w-28 truncate font-mono text-gray-700">{t.voter_key}</span>
                <code className="min-w-0 flex-1 truncate rounded bg-white px-2 py-1 text-gray-600">
                  {t.vote_url}
                </code>
                <button
                  onClick={() => {
                    void navigator.clipboard?.writeText(t.vote_url);
                    notify.success('Link copied');
                  }}
                  className="shrink-0 rounded p-1 text-emerald-700 hover:bg-emerald-100"
                >
                  <ClipboardCopy className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Calendar-invite delivery report */}
      {inviteResult && (
        <div className="mb-3 rounded-lg border border-blue-200 bg-blue-50 p-3">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold text-blue-800">
              {inviteResult.invited} invite{inviteResult.invited === 1 ? '' : 's'} emailed
              {inviteResult.failed.length > 0 && ` · ${inviteResult.failed.length} failed`}
              {inviteResult.skipped.length > 0 && ` · ${inviteResult.skipped.length} without email`}
            </p>
            <button
              onClick={() => setInviteResult(null)}
              className="text-xs text-blue-600 hover:text-blue-800"
            >
              Dismiss
            </button>
          </div>
          {(inviteResult.skipped.length > 0 || inviteResult.failed.length > 0) && (
            <div className="mt-2 max-h-32 space-y-1 overflow-y-auto">
              {inviteResult.skipped.map((s) => (
                <p key={s.voter_key} className="text-xs text-amber-700">
                  {s.display_name || s.voter_key} — {s.reason} (issue a vote link instead)
                </p>
              ))}
              {inviteResult.failed.map((f) => (
                <p key={f.voter_key} className="text-xs text-red-600">
                  {f.voter_key} ({f.email}) — {f.error}
                </p>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Arrears-notice delivery report — the campaign itself is tracked in
          Communications Management as an Arrears Reminder work item. */}
      {arrearsResult && (
        <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold text-amber-800">
              Arrears notice queued to {arrearsResult.queued} voter{arrearsResult.queued === 1 ? '' : 's'}
              {arrearsResult.scheduled_at && ` · sends ${formatDateTime(arrearsResult.scheduled_at)}`}
              {arrearsResult.skipped.length > 0 && ` · ${arrearsResult.skipped.length} without email`}
            </p>
            <button
              onClick={() => setArrearsResult(null)}
              className="text-xs text-amber-700 hover:text-amber-900"
            >
              Dismiss
            </button>
          </div>
          {arrearsResult.skipped.length > 0 && (
            <div className="mt-2 max-h-32 space-y-1 overflow-y-auto">
              {arrearsResult.skipped.map((s) => (
                <p key={s.voter_key} className="text-xs text-amber-700">
                  {s.display_name || s.voter_key} — {s.reason} (call or WhatsApp them instead)
                </p>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Roll table */}
      {loading ? (
        <div className="flex items-center justify-center py-12 text-gray-400">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : error ? (
        <div className="text-sm text-red-600">{error}</div>
      ) : voters.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-12 text-center">
          <Users className="h-8 w-8 text-gray-300" />
          <p className="max-w-sm text-sm text-gray-500">
            No voters on the roll yet.
            {session.delivery_mode === 'meeting'
              ? session.voting_opens_at != null
                ? ' Add voters, then issue vote links for the pre-voting window — meeting joiners also check in automatically.'
                : ' Members who join the meeting check in automatically — add entries here to pre-load eligibility and weights.'
              : ' Add voters, then issue each a vote-by-link.'}
          </p>
        </div>
      ) : (
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-400">
              <th className="py-2 pr-3 font-medium">Voter</th>
              <th className="py-2 pr-3 font-medium">Member ref</th>
              <th className="py-2 pr-3 font-medium">Weight</th>
              <th className="py-2 pr-3 font-medium">Status</th>
              <th className="py-2 pr-3 font-medium">Checked in</th>
              {editable && <th className="py-2 font-medium"></th>}
            </tr>
          </thead>
          <tbody>
            {voters.map((v) => (
              <tr key={v.id} className="border-b border-gray-100 last:border-0">
                <td className="py-2 pr-3">
                  <div className="font-medium text-gray-900">{v.display_name || '—'}</div>
                  <div className="font-mono text-xs text-gray-500">{v.voter_key}</div>
                </td>
                <td className="py-2 pr-3 text-gray-600">{v.member_ref || '—'}</td>
                <td className="py-2 pr-3 text-gray-600">{v.weight}</td>
                <td className="py-2 pr-3">
                  <Badge
                    label={v.eligible ? 'Eligible' : 'Ineligible'}
                    color={v.eligible ? '#166534' : '#b91c1c'}
                    bg={v.eligible ? '#dcfce7' : '#fee2e2'}
                  />
                  {v.proxy_of && (
                    <span className="ml-1 text-xs text-gray-400">delegated to {v.proxy_of}</span>
                  )}
                  {(() => {
                    const held = voters.filter((x) => x.proxy_of === v.voter_key).length;
                    return held > 0 ? (
                      <span
                        className="ml-1 rounded-full bg-indigo-100 px-1.5 py-0.5 text-xs font-medium text-indigo-700"
                        title={`Holds ${held} prox${held === 1 ? 'y' : 'ies'} — their ballot carries the delegated weight`}
                      >
                        holds {held} prox{held === 1 ? 'y' : 'ies'}
                      </span>
                    ) : null;
                  })()}
                </td>
                <td className="py-2 pr-3 text-xs text-gray-500">
                  {v.checked_in_at ? (
                    <span className="inline-flex items-center gap-1 text-emerald-700">
                      <UserCheck className="h-3.5 w-3.5" /> {formatDateTime(v.checked_in_at)}
                    </span>
                  ) : (
                    '—'
                  )}
                </td>
                {editable && (
                  <td className="py-2 text-right">
                    <button
                      onClick={() => void toggleEligible(v)}
                      className={`inline-flex items-center gap-1 rounded-lg border px-2 py-1 text-xs font-medium ${
                        v.eligible
                          ? 'border-red-200 text-red-600 hover:bg-red-50'
                          : 'border-emerald-200 text-emerald-700 hover:bg-emerald-50'
                      }`}
                    >
                      {v.eligible ? <Trash2 className="h-3 w-3" /> : <CheckCircle2 className="h-3 w-3" />}
                      {v.eligible ? 'Disqualify' : 'Reinstate'}
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
